import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Avatar, Button, Modal, resolveErrorMessage, SearchInput, useTranslate } from "@tovi/ui";
import type { MessageKey } from "@tovi/shared";
import { approveCode, claimCode, denyCode } from "@/lib/device.ts";
import { overrideCode } from "@/lib/override.ts";
import { useEmployeePhoto } from "@/lib/photo.ts";
import { api } from "@/lib/api.ts";

/**
 * Taking a till by approving its device code (`#213`).
 *
 * The code itself, large, is the only proof this screen offers — never a till
 * name or the code's `scope`: anyone can request a device code with whatever
 * scope they like, so printing "the till at …" would hand a phisher the words
 * to put there instead. Matching the code against the till's own screen is
 * the approver's whole check, and the copy says so.
 *
 * Approving only identifies the approver: whether they take the till, check
 * in, do both, or check out is a choice the till puts to them afterwards (`#229`),
 * which is why the button says "It's me" rather than promising either. For a
 * manager holding `terminals:assign_staff` there is also putting somebody
 * else on the till instead (`#230`): the reason the whole scan design is safe
 * to ship, since a person whose own phone cannot get on still needs a way
 * onto a till.
 */

type Outcome = "notForTill" | "taken" | "used" | "gone" | "refused";

const OUTCOME_KEYS: Record<Outcome, MessageKey> = {
  notForTill: "staff.approve.notForTill",
  taken: "staff.approve.taken",
  used: "staff.approve.used",
  gone: "staff.approve.gone",
  refused: "staff.approve.refused",
};

type Phase =
  | { kind: "claiming" }
  | { kind: "offline" }
  | { kind: "ready" }
  | { kind: "approved" }
  | { kind: "assigned"; name: string }
  | { kind: Outcome };

function groupedCode(userCode: string): string {
  const bare = userCode.replace(/[^a-zA-Z0-9]/g, "");
  return bare.length === 8 ? `${bare.slice(0, 4)}-${bare.slice(4)}` : userCode;
}

function Screen({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
      {children}
    </div>
  );
}

export function ApproveScreen({
  userCode,
  onFinished,
}: {
  userCode: string;
  onFinished: () => void;
}) {
  const t = useTranslate();
  const [phase, setPhase] = useState<Phase>({ kind: "claiming" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void claimCode(userCode).then((result) => setPhase({ kind: result.kind }));
  }, [userCode]);

  function retry(): void {
    setPhase({ kind: "claiming" });
    void claimCode(userCode).then((result) => setPhase({ kind: result.kind }));
  }

  async function take(): Promise<void> {
    setBusy(true);
    const result = await approveCode(userCode);
    setBusy(false);
    setPhase({ kind: result.kind === "done" ? "approved" : result.kind });
  }

  async function decline(): Promise<void> {
    setBusy(true);
    const result = await denyCode(userCode);
    setBusy(false);
    if (result.kind === "done") {
      onFinished();
      return;
    }
    setPhase({ kind: result.kind });
  }

  if (phase.kind === "claiming") {
    return (
      <Screen>
        <p className="text-sm text-ink-muted">{t("staff.approve.checking")}</p>
      </Screen>
    );
  }

  if (phase.kind === "offline") {
    return (
      <Screen>
        <p className="text-sm text-danger">{t("staff.signIn.unreachable")}</p>
        <Button variant="secondary" onClick={retry}>
          {t("staff.approve.retry")}
        </Button>
      </Screen>
    );
  }

  if (phase.kind === "ready") {
    return (
      <Screen>
        <p className="text-sm text-ink-muted">{t("staff.approve.instruction")}</p>
        <p className="text-4xl font-semibold tracking-wide text-ink-body">
          {groupedCode(userCode)}
        </p>
        <div className="flex w-full max-w-xs flex-col gap-3">
          <Button loading={busy} onClick={() => void take()}>
            {t("staff.approve.take")}
          </Button>
          <Button variant="secondary" disabled={busy} onClick={() => void decline()}>
            {t("staff.approve.notMe")}
          </Button>
          <SomeoneElseButton
            userCode={userCode}
            onAssigned={(name) => setPhase({ kind: "assigned", name })}
          />
        </div>
      </Screen>
    );
  }

  if (phase.kind === "approved") {
    return (
      <Screen>
        <p className="text-sm text-ink-body">{t("staff.approve.approved")}</p>
        <Button variant="secondary" onClick={onFinished}>
          {t("staff.approve.backHome")}
        </Button>
      </Screen>
    );
  }

  if (phase.kind === "assigned") {
    return (
      <Screen>
        <p className="text-sm text-ink-body">{t("staff.approve.assigned", { name: phase.name })}</p>
        <Button variant="secondary" onClick={onFinished}>
          {t("staff.approve.backHome")}
        </Button>
      </Screen>
    );
  }

  return (
    <Screen>
      <p className="text-sm text-ink-body">{t(OUTCOME_KEYS[phase.kind])}</p>
      <Button variant="secondary" onClick={onFinished}>
        {t("staff.approve.backHome")}
      </Button>
    </Screen>
  );
}

type StaffCandidate = { id: string; name: string; photoVersion: string | null };

/**
 * "Someone else" (`#230`) — hidden entirely rather than shown disabled or
 * erroring, for two separate reasons that both land on the same outcome: a
 * waiter without `terminals:assign_staff` never sees it, and a manager who
 * holds the permission but not `staff:read` (a role edited to carry one
 * without the other) cannot open a picker with nothing in it either. That
 * second case is worth a dedicated candidates route a maintainer may want to
 * add later — this reuses `GET /store/staff` rather than build one.
 *
 * `["me"]` is the same query key `home.tsx` fetches with, so the two screens
 * share one request rather than each firing their own.
 */
function SomeoneElseButton({
  userCode,
  onAssigned,
}: {
  userCode: string;
  onAssigned: (name: string) => void;
}) {
  const t = useTranslate();
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api<{ permissions: string[] }>("/me"),
  });
  const canOverride = me.data?.permissions.includes("terminals:assign_staff") ?? false;
  const staff = useQuery({
    queryKey: ["staff-override-candidates"],
    queryFn: () =>
      api<{ data: StaffCandidate[] }>("/store/staff?status=active&size=200").then(
        (page) => page.data,
      ),
    enabled: canOverride,
  });
  const [open, setOpen] = useState(false);

  if (!canOverride || !staff.data) return null;

  return (
    <>
      <Button variant="ghost" onClick={() => setOpen(true)}>
        {t("staff.approve.someoneElse")}
      </Button>
      {open ? (
        <PickEmployeeModal
          userCode={userCode}
          employees={staff.data}
          onClose={() => setOpen(false)}
          onAssigned={(name) => {
            setOpen(false);
            onAssigned(name);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * The picker itself: a search over the store's active employees, then one
 * confirmation before the call actually runs — asked once, as `#230` puts
 * it, never silently.
 */
function PickEmployeeModal({
  userCode,
  employees,
  onClose,
  onAssigned,
}: {
  userCode: string;
  employees: StaffCandidate[];
  onClose: () => void;
  onAssigned: (name: string) => void;
}) {
  const t = useTranslate();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<StaffCandidate | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? employees.filter((e) => e.name.toLowerCase().includes(needle))
    : employees;

  async function confirm(): Promise<void> {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const result = await overrideCode(userCode, selected.id);
      if (result.kind === "done") {
        onAssigned(selected.name);
        return;
      }
      setError(
        t(
          result.kind === "gone"
            ? "error.deviceCodeNotPending"
            : result.kind === "refused"
              ? "error.forbidden"
              : "staff.approve.assignRefused",
        ),
      );
    } catch (caught) {
      setError(resolveErrorMessage(caught, t, "staff.approve.assignRefused"));
    } finally {
      setBusy(false);
    }
  }

  if (selected) {
    return (
      <Modal
        open
        onClose={onClose}
        title={t("staff.approve.pickEmployee")}
        footer={
          <>
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => setSelected(null)}>
              {t("action.cancel")}
            </Button>
            <Button size="sm" loading={busy} onClick={() => void confirm()}>
              {t("action.yes")}
            </Button>
          </>
        }
      >
        <div className="flex flex-col items-center gap-3 py-2 text-center">
          <Avatar name={selected.name} size={56} />
          <p className="text-sm text-ink-body">
            {t("staff.approve.confirmFor", { name: selected.name })}
          </p>
          {error ? <p className="text-sm text-danger">{error}</p> : null}
        </div>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} title={t("staff.approve.pickEmployee")}>
      <div className="flex flex-col gap-3">
        <SearchInput
          placeholder={t("staff.approve.search")}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <ul className="flex max-h-72 flex-col gap-1 overflow-y-auto">
          {filtered.map((employee) => (
            <EmployeeRow
              key={employee.id}
              employee={employee}
              onSelect={() => setSelected(employee)}
            />
          ))}
        </ul>
      </div>
    </Modal>
  );
}

function EmployeeRow({ employee, onSelect }: { employee: StaffCandidate; onSelect: () => void }) {
  const photo = useEmployeePhoto(employee.id, employee.photoVersion);

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className="flex w-full items-center gap-3 rounded-control p-2 text-start hover:bg-chrome active:bg-chrome-active"
      >
        <Avatar name={employee.name} src={photo ?? undefined} size={36} />
        <span className="text-sm font-medium text-ink-body">{employee.name}</span>
      </button>
    </li>
  );
}
